/** @file Normalizes RGBA pixels and encodes them as an 8-bit PNG in row bands (the upload format). */
import { createPngWriter } from '@pose2d/image-io/createPngWriter';
import { normalizeImage } from '@pose2d/image-io/normalizeImage';
import type { RgbaImage } from '@pose2d/math/types';

/** Rows per writeRows call: a band of a 2048 px wide layer is 512 KiB. */
const BAND_ROWS = 64;

/**
 * Straight alpha, rgb zeroed under alpha 0 (normalizeImage copies first, so a pooled readPsdLayer view may be reused
 * as soon as this returns its promise), then PNG; the bytes are what the Worker hashes and stores (spec §8.7).
 */
export async function encodeRgbaPng(image: RgbaImage): Promise<Uint8Array<ArrayBuffer>> {
  const { width, height, data } = normalizeImage(image);
  const writer = createPngWriter(width, height);
  const stride = width * 4;
  for (let y = 0; y < height; y += BAND_ROWS) {
    const rows = Math.min(BAND_ROWS, height - y);
    await writer.writeRows(data.subarray(y * stride, (y + rows) * stride), rows);
  }
  return new Uint8Array(await writer.finish());
}
